import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { getRentalPortalApplicantEmails } from '../../../../api'
import { getQaSelector } from '../../../../utils/common'
import Button from '../../../Button'
import { PageEmptyState } from '../../../PageEmptyState'
import { getIsApplicantFieldMasked } from '../../../RentalPortalApplicant'
import { Table } from '../../../Table'
import { TablePagination } from '../../../TablePagination'
import Text from '../../../Text'
import { useRentalPortalPageRead } from '../../RentalPortal.hooks'
import { useApplicantProfileContext } from '../RentalPortalApplicantProfile.hooks'

import { ApplicantProfileEmailsComposeButton } from './ApplicantProfileEmailsComposeButton'
import { ApplicantProfileEmailsRow } from './ApplicantProfileEmailsRow'
import {
  APPLICANT_PROFILE_EMAILS_COLUMNS_CONFIG,
  APPLICANT_PROFILE_EMAILS_PAGE_SIZE,
  APPLICANT_PROFILE_EMAILS_SKELETON_ROWS,
  QA_NAME_APPLICANT_PROFILE_EMAILS,
  QA_NAME_APPLICANT_PROFILE_EMAILS_RETRY,
  QA_NAME_APPLICANT_PROFILE_EMAILS_TABLE,
} from './RentalPortalApplicantProfileEmails.constants'
import { getIsApplicantProfileEmailsEmpty } from './RentalPortalApplicantProfileEmails.utils'

import * as styles from './RentalPortalApplicantProfileEmails.module.css'

import EmailIcon from '../../../../icons/email.svg'
import RefreshIcon from '../../../../icons/refresh.svg'
import RentalPortalIcon from '../../../../icons/rental-portal.svg'

function RentalPortalApplicantProfileEmails() {
  const { t } = useTranslation()

  const { applicantData } = useApplicantProfileContext()

  const containerRef = useRef(null)

  const applicantId = applicantData?.id

  async function readEmails({ page, size }) {
    return getRentalPortalApplicantEmails({ applicantId, page, size })
  }

  const {
    handleRetry,
    handleSetPagination,
    hasError,
    isInitiated,
    isLoading,
    items,
    pagination,
  } = useRentalPortalPageRead({
    isEnabled: Boolean(applicantId),
    isUrlBound: false,
    pageSize: APPLICANT_PROFILE_EMAILS_PAGE_SIZE,
    read: readEmails,
    readParams: [
      applicantId,
      applicantData?.counts?.emails,
      getIsApplicantFieldMasked(applicantData?.identity),
    ],
  })

  return (
    <div
      {...getQaSelector({ value: QA_NAME_APPLICANT_PROFILE_EMAILS })}
      className={styles.RentalPortalApplicantProfileEmails}
      ref={containerRef}
    >
      <ApplicantProfileEmailsComposeButton className={styles.composeButton} />
      {hasError ? (
        <PageEmptyState EntityIcon={RentalPortalIcon}>
          <Text isSecondary>
            {t('RentalPortal.ApplicantProfileEmailsLoadErrorKey')}
          </Text>
          <Button.Flat
            beforeIconProps={{ Icon: RefreshIcon }}
            isLoading={isLoading}
            qaName={QA_NAME_APPLICANT_PROFILE_EMAILS_RETRY}
            text={t('RentalPortal.ListingRetryBtnKey')}
            onClick={handleRetry}
          />
        </PageEmptyState>
      ) : getIsApplicantProfileEmailsEmpty({ emails: items, isInitiated }) ? (
        <PageEmptyState
          EntityIcon={EmailIcon}
          text={t('RentalPortal.ApplicantProfileEmailsEmptyKey')}
        />
      ) : (
        <>
          <Table
            TableRow={ApplicantProfileEmailsRow}
            actionsColumnWidth={0}
            className={styles.table}
            columns={APPLICANT_PROFILE_EMAILS_COLUMNS_CONFIG}
            containerRef={containerRef}
            context={QA_NAME_APPLICANT_PROFILE_EMAILS_TABLE}
            headerRowProps={{ className: styles.row, resizable: false }}
            isLoading={isLoading}
            items={items}
            skeletonProps={{ withCheckbox: false }}
            skeletonRows={APPLICANT_PROFILE_EMAILS_SKELETON_ROWS}
          />
          <TablePagination
            responsive
            containerRef={containerRef}
            isSkeleton={!isInitiated}
            value={pagination}
            onChange={handleSetPagination}
          />
        </>
      )}
    </div>
  )
}

export default RentalPortalApplicantProfileEmails
